import { canonicalProductSlug } from '../lib/slug';

/**
 * The knowledge base: general agronomic science, written to be genuinely useful on its own.
 *
 * WHY IT IS SEPARATE FROM THE PRODUCTS. A page that explains the rhizosphere and a page that
 * specifies a product are two different kinds of claim. The science below is settled, general and
 * carries no GreenbayBio promise; the product record is specific, verified and carries one. Mixing
 * them is how "beneficial microbes can improve nutrient availability" quietly becomes "this product
 * improves your nutrient availability" — a claim nobody measured. So each article explains the
 * mechanism first and only then names the GreenbayBio products that belong to the category it
 * describes, as links, with no performance claim attached.
 *
 * HOW THE CONTENT IS BOUNDED.
 *   - Mechanisms, definitions and classifications are standard agronomy, stated in hedged language
 *     where the literature itself is not settled ("commonly reported", "estimates vary").
 *   - Regulatory statements name the instrument (Fertiliser (Control) Order, NPOP) and say who
 *     administers it. They never state what a particular product holds — that lives on the product
 *     page, from the product record.
 *   - Figures that belong to a GreenbayBio product (a CFU count, a dose) are quoted only where the
 *     product sheet states them, and attributed to it.
 *   - `sources` names institutions and instruments rather than linking to urls that could not be
 *     verified from this environment. Adding the verified deep links is an owner follow-up.
 *   - There are no named authors. The publisher is the company; inventing an agronomist to sign an
 *     article would be inventing an expert.
 */

export interface KnowledgeSection {
  heading: string;
  paras?: string[];
  bullets?: string[];
  /** A short definition list, for classifications that read better as terms than as prose. */
  terms?: { term: string; detail: string }[];
}

export interface KnowledgeArticle {
  slug: string;
  /** The H1. Phrased as the question a reader actually types. */
  title: string;
  /** Meta description / card summary. */
  summary: string;
  /**
   * The direct answer. One self-contained paragraph, first thing after the H1, so an answer engine
   * can lift a correct answer without reading the rest of the page.
   */
  answer: string;
  section: 'Soil biology' | 'Bio-inputs' | 'Application practice';
  /** ISO date the text last changed. Real, not decorative — it drives sitemap lastmod. */
  updated: string;
  body: KnowledgeSection[];
  /** Where the GreenbayBio range sits in this subject. Category statements, never performance claims. */
  rangeNote?: string;
  relatedProductSlugs: string[];
  relatedCategorySlugs: string[];
  relatedSlugs: string[];
  faq: { q: string; a: string }[];
  sources: string[];
  keywords: string[];
}

const FCO = 'Fertiliser (Control) Order, 1985 — the Indian order that specifies fertiliser and bio-fertiliser standards';
const NPOP =
  'National Programme for Organic Production (NPOP) — India’s organic standard, administered by APEDA under the Ministry of Commerce & Industry';
const EUFPR =
  'EU Regulation 2019/1009 on fertilising products — the regulation that defines a “plant biostimulant” in law';

export const knowledgeArticles: KnowledgeArticle[] = [
  // ─────────────────────────────────────────────────────────── soil biology
  {
    slug: 'soil-biology',
    title: 'What is soil biology?',
    summary:
      'Soil biology is the living part of soil — bacteria, fungi, protozoa, nematodes and larger animals — and the processes they drive: decomposition, nutrient cycling, soil structure and disease suppression.',
    answer:
      'Soil biology is the living component of soil and what it does. It covers bacteria, archaea, fungi, protozoa, nematodes, microarthropods and earthworms, together with the processes they drive: decomposing organic matter, cycling nitrogen, phosphorus and sulphur into forms roots can take up, binding soil particles into stable aggregates, and competing with or suppressing organisms that cause root disease. Soil chemistry and soil physics describe what soil is made of and how it is arranged; soil biology describes what is alive in it and what that life does to the other two.',
    section: 'Soil biology',
    updated: '2026-09-12',
    body: [
      {
        heading: 'What lives in soil',
        paras: [
          'Soil is not an inert growing medium. It is a habitat, and the organisms in it span more than six orders of magnitude in size — from single bacterial cells to earthworms. Estimates of microbial numbers vary widely with soil type, season and measurement method, but a gram of productive topsoil is generally described in the soil-science literature as holding hundreds of millions to billions of bacterial cells, alongside metres of fungal hyphae.',
          'The important point for a grower is not the count but the function. Different groups do different jobs, and a soil can be rich in organic matter yet biologically inactive if the organisms that process it are absent or suppressed.',
        ],
        terms: [
          {
            term: 'Bacteria and archaea',
            detail:
              'The most numerous group. Between them they drive nitrogen fixation, nitrification, sulphur oxidation, and much of the solubilisation and mineralisation of phosphorus.',
          },
          {
            term: 'Fungi',
            detail:
              'Decompose the tougher fractions of organic matter — cellulose and lignin — that most bacteria cannot. Mycorrhizal fungi form partnerships with roots and extend the volume of soil a plant can draw water and nutrients from.',
          },
          {
            term: 'Protozoa and nematodes',
            detail:
              'Graze on bacteria and fungi. That grazing is itself a nutrient-release mechanism: nitrogen held in microbial cells is excreted in plant-available form near the roots.',
          },
          {
            term: 'Earthworms and microarthropods',
            detail:
              'Fragment residues, mix soil layers and build channels that carry water and air into the profile.',
          },
        ],
      },
      {
        heading: 'The four jobs soil biology does',
        bullets: [
          'Decomposition — breaking crop residues and organic inputs down into humified organic matter and releasing the nutrients locked inside them.',
          'Nutrient cycling — converting nutrients between forms. Nitrogen fixation, nitrification and phosphorus solubilisation are all biological steps; without them, much of the nutrient stock in soil is present but unavailable.',
          'Soil structure — microbial gums and fungal hyphae bind particles into aggregates. Aggregated soil holds more water, drains better and resists crusting and erosion.',
          'Disease suppression — a dense, diverse community occupies space and consumes resources that a root pathogen would otherwise use. This is competitive exclusion, and it is why biologically active soils are often described as suppressive.',
        ],
      },
      {
        heading: 'What suppresses soil biology',
        paras: [
          'Soil biology declines under conditions that are common in intensive cultivation: low organic-matter return, repeated deep tillage, prolonged waterlogging or compaction, and salinity. Salt accumulation — whether from heavy fertiliser use or from irrigating with saline water — raises osmotic stress on microbes as well as on roots, and is one of the more frequent reasons a field loses productivity without any obvious nutrient deficiency.',
          'Recovery is slow relative to a cropping season, because it depends on rebuilding both the organic matter that feeds the community and the community itself.',
        ],
      },
      {
        heading: 'How soil biology is assessed',
        paras: [
          'A standard soil test reports chemistry — pH, electrical conductivity, organic carbon, available N, P and K — and says nothing directly about biology. Biological assessment uses different measures: microbial biomass carbon, soil respiration, enzyme activities such as dehydrogenase and phosphatase, and counts or DNA-based profiles of particular groups.',
          'These measures are more variable than chemical ones and are usually read as trends over seasons rather than as single pass/fail numbers.',
        ],
      },
    ],
    rangeNote:
      'GreenbayBio’s soil-biology and soil-improver products are the part of the range aimed at this subject: bio-fertilizers that introduce specified beneficial bacteria into the root zone, and organic soil improvers that add organic matter and a mixed microbial population. What each one contains, at what count, and how it is applied is stated on its own product page.',
    relatedProductSlugs: ['agn', 'agn-lte', 'biodynamic', 'biovin'],
    relatedCategorySlugs: ['soil-biology', 'soil-improvers'],
    relatedSlugs: ['rhizosphere', 'bio-fertilizers', 'phosphate-solubilizing-bacteria'],
    faq: [
      {
        q: 'Is soil biology the same as soil health?',
        a: 'No. Soil health is the broader idea — the capacity of a soil to function — and includes its physical and chemical condition as well as its biology. Soil biology is one of the three, and the one that most directly influences the other two over time.',
      },
      {
        q: 'Can soil biology be rebuilt?',
        a: 'Biological activity responds to management: returning organic matter, reducing disturbance, keeping the soil covered and correcting waterlogging or salinity all favour it. Recovery is measured in seasons rather than weeks, and how far it goes depends on the soil and the climate.',
      },
      {
        q: 'Does adding microbes to soil always increase activity?',
        a: 'Not necessarily. Introduced organisms have to survive, find a niche and compete with what is already there. Survival depends on moisture, temperature, organic-matter supply and the chemistry of the soil they land in — which is why application instructions specify timing, dilution and method rather than leaving them to the user.',
      },
    ],
    sources: [
      'Standard soil-microbiology texts and extension literature on soil biological function',
      'Food and Agriculture Organization of the United Nations (FAO) — soils and global soil partnership programmes',
      'Indian Council of Agricultural Research (ICAR) — soil science and microbiology research institutes',
    ],
    keywords: ['soil biology', 'soil microorganisms', 'soil health', 'nutrient cycling', 'competitive exclusion'],
  },

  // ─────────────────────────────────────────────────────────── rhizosphere
  {
    slug: 'rhizosphere',
    title: 'What is the rhizosphere?',
    summary:
      'The rhizosphere is the thin zone of soil around a living root that the root itself changes — through exudates, water uptake and respiration — and where most plant–microbe interaction happens.',
    answer:
      'The rhizosphere is the narrow zone of soil immediately surrounding a living root, typically a few millimetres thick, whose chemistry and biology are altered by the root itself. Roots release a mixture of sugars, organic acids, amino acids and other compounds into this zone, take up water and nutrients from it, and respire into it. The result is a habitat far richer in carbon and far denser in microbial life than the bulk soil a few centimetres away. The term was introduced by the German agronomist Lorenz Hiltner in 1904.',
    section: 'Soil biology',
    updated: '2026-09-12',
    body: [
      {
        heading: 'Why the rhizosphere is different',
        paras: [
          'Bulk soil is carbon-poor: most microbes in it are dormant much of the time because there is nothing to eat. A root changes that locally. Plants release a share of the carbon they fix in photosynthesis through their roots — the proportion is difficult to measure and reported figures vary considerably, commonly in the range of a few per cent up to around a fifth of fixed carbon depending on species, age and conditions.',
          'That carbon supply supports microbial populations around the root that can be one to two orders of magnitude denser than in bulk soil. It also makes the rhizosphere the place where competition between beneficial organisms and root pathogens is actually decided.',
        ],
      },
      {
        heading: 'What happens in it',
        terms: [
          {
            term: 'Exudation',
            detail:
              'Roots release sugars, organic acids, amino acids, phenolics and enzymes. Some of this is leakage; some is active and specific — organic acids that free phosphorus from mineral surfaces, or signals that recruit particular microbial partners.',
          },
          {
            term: 'pH change',
            detail:
              'Nutrient uptake is charge-balanced by releasing protons or hydroxide, so a root can shift the pH of its immediate surroundings by a unit or more. That shift changes how soluble phosphorus, iron, zinc and manganese are right where the root is.',
          },
          {
            term: 'Depletion and accumulation',
            detail:
              'Mobile nutrients such as nitrate arrive with water flow and can accumulate at the root surface; immobile ones such as phosphorus are taken up faster than they diffuse, creating a depletion zone the root must grow through or reach past.',
          },
          {
            term: 'Colonisation',
            detail:
              'Free-living bacteria and fungi, nitrogen-fixing symbionts and mycorrhizal fungi establish here. So do pathogens — which is why what already occupies the zone matters.',
          },
        ],
      },
      {
        heading: 'Competitive exclusion, plainly',
        paras: [
          'Competitive exclusion is the observation that two organisms needing the same limited resource in the same place cannot both thrive. In the rhizosphere the limited resources are root-surface space and root-derived carbon. A dense, established community of harmless or beneficial organisms leaves a pathogen less of both.',
          'It is a population effect, not a treatment: it reduces the likelihood and severity of infection under favourable conditions. It is not disease control, it does not replace a fungicide where one is warranted, and no biological input should be described as though it were.',
        ],
      },
      {
        heading: 'Why it matters for input decisions',
        paras: [
          'If the target of a product is the rhizosphere, then getting it to the rhizosphere is the whole task. That is why soil-applied microbial products specify a carrier volume, a dilution and an irrigation or drench method — the active organisms have to arrive in the root zone alive and in water, not sit on a dry surface. Products whose labels call for an incubation step before application are activating dormant cells for the same reason.',
        ],
      },
    ],
    rangeNote:
      'The bio-fertilizers in GreenbayBio’s soil-biology category are formulated for soil application rather than foliar spraying, because the rhizosphere is where their specified bacteria are intended to act. Their product pages state the dilution, the incubation step where one applies, and the irrigation methods documented for them.',
    relatedProductSlugs: ['agn', 'agn-lte', 'biodynamic', 'fulvic-25'],
    relatedCategorySlugs: ['soil-biology'],
    relatedSlugs: ['soil-biology', 'phosphate-solubilizing-bacteria', 'bio-fertilizers'],
    faq: [
      {
        q: 'How big is the rhizosphere?',
        a: 'It is usually described as extending one to a few millimetres from the root surface, but there is no sharp edge — the gradient in carbon, pH and microbial density simply fades into bulk soil. For mobile effects such as pH change it can extend further than for carbon supply.',
      },
      {
        q: 'Is the rhizosphere the same as the root zone?',
        a: 'No. The root zone is the whole volume of soil a crop’s roots occupy, which may be a metre deep or more. The rhizosphere is only the thin film of soil in contact with and immediately around each individual root.',
      },
      {
        q: 'Why do some soil products need to be watered in?',
        a: 'Because their target is the rhizosphere. Water is what carries a soil-applied product down through the profile to where roots actually are; without it the product stays near the surface, outside the zone it was formulated to act in.',
      },
    ],
    sources: [
      'Hiltner, L. (1904) — the paper that introduced the term rhizosphere',
      'Standard plant-nutrition and soil-microbiology texts on rhizosphere processes',
      'Food and Agriculture Organization of the United Nations (FAO) — soil biodiversity resources',
    ],
    keywords: ['rhizosphere', 'root zone', 'root exudates', 'competitive exclusion', 'soil microbes'],
  },

  // ─────────────────────────────────────────────────────────── bio-fertilizers
  {
    slug: 'bio-fertilizers',
    title: 'What are bio-fertilizers?',
    summary:
      'A bio-fertilizer is a preparation of living microorganisms that improves the supply or availability of nutrients to a crop. It is regulated, specified by microbial count, and works differently from a chemical fertiliser.',
    answer:
      'A bio-fertilizer is a preparation containing living microorganisms which, applied to seed, soil or plant surfaces, colonises the root zone and increases the supply or availability of nutrients to the crop. The microorganisms are the active ingredient: they fix atmospheric nitrogen, solubilise or mineralise phosphorus and potassium held in unavailable forms, or extend the soil volume a root can draw from. That is the distinction from a chemical fertiliser, which supplies the nutrient itself. A bio-fertilizer supplies the biological process that makes a nutrient available.',
    section: 'Bio-inputs',
    updated: '2026-09-12',
    body: [
      {
        heading: 'The main types',
        terms: [
          {
            term: 'Nitrogen fixers',
            detail:
              'Convert atmospheric nitrogen to ammonia. Symbiotic genera such as Rhizobium work inside legume root nodules; free-living and associative genera such as Azotobacter and Azospirillum work in the soil and rhizosphere.',
          },
          {
            term: 'Phosphate solubilisers and mobilisers',
            detail:
              'Release phosphorus that is chemically fixed in soil, mainly by producing organic acids and phosphatase enzymes. Bacillus and Pseudomonas species and some fungi are the commonly used organisms.',
          },
          {
            term: 'Potassium and zinc mobilisers',
            detail:
              'Weather minerals and release potassium or zinc into solution, by a comparable mechanism of acidification and chelation.',
          },
          {
            term: 'Mycorrhizal fungi',
            detail:
              'Form a symbiosis with roots. The fungal network explores a much larger soil volume than roots alone, which mainly benefits uptake of immobile nutrients such as phosphorus, and of water.',
          },
        ],
      },
      {
        heading: 'Bio-fertilizer and chemical fertiliser are not substitutes',
        paras: [
          'The two do different things. A chemical fertiliser adds a measured quantity of a nutrient. A bio-fertilizer adds a process that can make nutrients already present — in soil minerals, in organic matter, or in the atmosphere — available to a crop.',
          'That means the honest comparison is not which is better but what each is for. Bio-fertilizers are generally used to improve nutrient-use efficiency and soil biological condition alongside a nutrition programme, not to replace it. Reported responses vary substantially with soil type, nutrient status, climate and crop, and any claim of a fixed yield gain from a biological input should be treated with scepticism.',
        ],
      },
      {
        heading: 'Why the microbial count is on the label',
        paras: [
          'A bio-fertilizer is only as good as the number of viable organisms that reach the soil. So its specification is a count — colony-forming units per millilitre for a liquid, per gram for a carrier-based powder — measured at manufacture, with a shelf life over which that count is expected to hold.',
          'In India, minimum counts and other specifications for bio-fertilizers are set under the Fertiliser (Control) Order, and product labels state the count against that standard. A product sheet that gives no count is not telling you what you are buying.',
        ],
      },
      {
        heading: 'Formulation, shelf life and handling',
        paras: [
          'Liquid and carrier-based formulations both exist. Liquids are easier to apply through irrigation; carrier-based powders are broadcast or blended with soil, sand or fertiliser. Some liquid formulations hold the organisms as dormant, encapsulated spores to keep the count stable in storage, and call for mixing with water and incubating for a set time before application so the spores become active.',
          'Two handling rules recur across microbial products because the active ingredient is alive: keep them out of direct sunlight and away from freezing, and observe whatever interval the label specifies between the bio-fertilizer and any fungicide, pesticide or weedicide. GreenbayBio’s bio-fertilizer labels specify a gap of at least 48 hours either side.',
        ],
      },
      {
        heading: 'Regulation and organic use in India',
        paras: [
          'Two separate things are often confused. Registration under the Fertiliser (Control) Order is the regulatory permission to sell a product as a fertiliser in India, and it carries the product specification. Approval for use in organic farming under NPOP is a statement that an input may be used in a certified organic system.',
          'They are different instruments with different scopes, and a product may hold one, both or neither. Approvals held by an overseas standard — an EU regulation, or a United States programme — are also not Indian regulatory approval. Any product page that states an approval should name which instrument, for which product.',
        ],
      },
    ],
    rangeNote:
      'GreenbayBio’s bio-fertilizers are phosphate-solubilising products based on Bacillus amyloliquefaciens and Bacillus megaterium — AGN and AGN LTE as liquids, BIODYNAMIC as a carrier-based powder. Each product page states its own microbial count, its dilution and incubation instructions, its documented crops and doses, and the approval it holds.',
    relatedProductSlugs: ['agn', 'agn-lte', 'biodynamic'],
    relatedCategorySlugs: ['soil-biology'],
    relatedSlugs: ['phosphate-solubilizing-bacteria', 'soil-biology', 'biostimulants'],
    faq: [
      {
        q: 'What is the difference between a bio-fertilizer and a biostimulant?',
        a: 'A bio-fertilizer contains living microorganisms and acts by making nutrients available. A biostimulant acts on the plant’s own physiology — nutrient-use efficiency, stress tolerance or quality — independently of how much nutrient it contains, and need not be alive. Some products span both descriptions, which is why the label and the registration matter more than the marketing term.',
      },
      {
        q: 'Do bio-fertilizers replace urea or DAP?',
        a: 'No. They improve the availability and efficiency of nutrition rather than supplying nutrients in fertiliser quantities. They are used alongside a nutrition programme, and how much of that programme they let you do without varies too much by site to be stated as a rule.',
      },
      {
        q: 'Can a bio-fertilizer be mixed with a fungicide?',
        a: 'The active ingredient is alive, and many fungicides and pesticides will kill it. Labels therefore specify a gap — GreenbayBio’s bio-fertilizer labels say at least 48 hours before or after. Follow the interval printed on the pack you are using.',
      },
      {
        q: 'How long does a bio-fertilizer keep?',
        a: 'Its shelf life is the period over which the stated microbial count is expected to hold, and it is printed on the pack. Storage conditions matter: direct sunlight, heat and freezing all shorten it.',
      },
    ],
    sources: [
      FCO,
      NPOP,
      'Indian Council of Agricultural Research (ICAR) — bio-fertiliser research and extension material',
      'Peer-reviewed literature on plant growth-promoting rhizobacteria and microbial inoculants',
    ],
    keywords: [
      'bio-fertilizer',
      'biofertilizer',
      'biological fertilizer',
      'bio fertilizer vs chemical fertilizer',
      'FCO bio-fertilizer',
      'microbial inoculant',
    ],
  },

  // ─────────────────────────────────────────────────────────── biostimulants
  {
    slug: 'biostimulants',
    title: 'What are biostimulants?',
    summary:
      'A plant biostimulant improves nutrient-use efficiency, stress tolerance, quality or nutrient availability independently of its own nutrient content. Humic substances, amino acids, seaweed extracts and beneficial microbes are the main categories.',
    answer:
      'A plant biostimulant is an input that improves one or more of a crop’s nutrient-use efficiency, tolerance to abiotic stress, quality traits, or the availability of nutrients confined in soil — and does so independently of how much nutrient the product itself contains. That last clause is the definition’s whole point and is written into EU fertilising-product law: a biostimulant is judged by the process it influences in the plant or rhizosphere, not by an analysis of what is in the bottle. It is therefore a different category from a fertiliser, and a different category again from a plant-protection product, which acts against a pest or pathogen.',
    section: 'Bio-inputs',
    updated: '2026-09-12',
    body: [
      {
        heading: 'The recognised categories',
        paras: [
          'The scientific literature commonly groups biostimulants into a small number of categories by what they are made of. The grouping is descriptive rather than regulatory, and a real product can belong to more than one.',
        ],
        terms: [
          {
            term: 'Humic and fulvic substances',
            detail:
              'Humified organic fractions. Act largely through chelation of nutrients and effects on root development and membrane transport.',
          },
          {
            term: 'Protein hydrolysates and amino acids',
            detail:
              'Free amino acids and short peptides from hydrolysed plant or animal protein. Act as a directly usable nitrogen form, as chelating agents, and as signals.',
          },
          {
            term: 'Seaweed and botanical extracts',
            detail:
              'Complex mixtures carrying polysaccharides, betaines and hormone-like compounds. Most often associated with abiotic-stress responses.',
          },
          {
            term: 'Chitosan and other biopolymers',
            detail:
              'Elicit defence-related responses in the plant and influence cell-wall and membrane behaviour.',
          },
          {
            term: 'Inorganic compounds',
            detail:
              'Beneficial elements — silicon is the common example — that are not essential nutrients but strengthen structural and stress responses.',
          },
          {
            term: 'Beneficial fungi and bacteria',
            detail:
              'Mycorrhizae and plant growth-promoting bacteria. These overlap with bio-fertilizers, and which term applies depends on the claimed mode of action and the registration.',
          },
        ],
      },
      {
        heading: 'What "abiotic stress tolerance" actually means',
        paras: [
          'Abiotic stress is stress from the physical environment rather than from a living pest: heat, cold, drought, waterlogging, salinity, high light. Crops respond to it with measurable physiological changes — stomatal closure, osmolyte accumulation, antioxidant activity, shifts in hormone balance — and a biostimulant that improves tolerance does so by influencing those responses.',
          'Tolerance is a matter of degree, and it is conditional. A product that helps a crop hold its metabolism together through a cold spell is not a guarantee against frost damage, and should not be read as one. Trial results in this area are also notably site- and season-dependent, which is why a specific yield figure from one trial is a poor basis for a general claim.',
        ],
      },
      {
        heading: 'Why "independently of nutrient content" matters commercially',
        paras: [
          'It sets the burden of evidence. If a product is sold as a fertiliser, its worth can be read off its analysis. If it is sold as a biostimulant, the analysis alone does not establish anything — the claim is about a process, and the evidence has to be about that process.',
          'For a buyer this has a practical consequence: on a biostimulant, the useful things on a label are the composition, the concentration, the crops and stages it is documented for, and the rate. Those are checkable. A percentage improvement with no stated conditions is not.',
        ],
      },
      {
        heading: 'Where biostimulants fit in a programme',
        paras: [
          'Biostimulants are generally applied at defined crop stages rather than continuously — at establishment, before or around flowering, during fruit development, or ahead of a predictable stress period — because the physiological processes they influence are stage-dependent. Rates are typically low relative to fertilisers, and many are applied either as a foliar spray or through irrigation.',
          'Compatibility with the rest of the tank is product-specific. Some biostimulants are explicitly compatible with fertilisers, microbial products and plant-protection products; others are not, and some have a limited window between mixing and application. This is label information, not something to reason out.',
        ],
      },
    ],
    rangeNote:
      'GreenbayBio’s biostimulant category holds Fulvic 25 (fulvic and humic substances) and AminOPF (a plant-source protein hydrolysate); the plant-strength category holds foliar products acting on the plant’s own metabolism and defences. Each product page carries its composition, specifications, documented crops, rate and application method.',
    relatedProductSlugs: ['fulvic-25', 'aminopf', 'c4l-biofoliar', 'nature-s-green-4-plus', 'plant-armor-elite'],
    relatedCategorySlugs: ['biostimulants', 'plant-strength'],
    relatedSlugs: ['humic-and-fulvic-substances', 'amino-acid-biostimulants', 'bio-fertilizers'],
    faq: [
      {
        q: 'Is a biostimulant a fertiliser?',
        a: 'No, though a single product can be registered as one and described as the other. A fertiliser supplies nutrients; a biostimulant influences how the plant acquires or uses them, or how it withstands stress, independently of its own nutrient content.',
      },
      {
        q: 'Is a biostimulant a pesticide?',
        a: 'No. A plant-protection product acts against a pest, weed or pathogen and is regulated on that basis. A biostimulant acts on the crop’s own physiology. A product that strengthens a plant’s defence responses is still not a pesticide and must not be described as controlling a disease.',
      },
      {
        q: 'Are biostimulants allowed in organic farming?',
        a: 'Some are and some are not — it depends on what the product is made of and on the standard in question. Approval is granted per product, so the only reliable answer is the approval named on that product’s own page or label.',
      },
    ],
    sources: [
      EUFPR,
      'du Jardin, P. (2015) — the review that established the widely used categorisation of plant biostimulants',
      'Peer-reviewed literature on biostimulant modes of action and abiotic-stress physiology',
    ],
    keywords: ['biostimulant', 'plant biostimulant', 'abiotic stress tolerance', 'nutrient use efficiency'],
  },

  // ─────────────────────────────────────────── phosphate solubilizing bacteria
  {
    slug: 'phosphate-solubilizing-bacteria',
    title: 'What are phosphate-solubilizing bacteria (PSB)?',
    summary:
      'PSB are soil bacteria that release phosphorus already present in soil but chemically locked up — by secreting organic acids and phosphatase enzymes. They are the basis of a large share of India’s registered bio-fertilizers.',
    answer:
      'Phosphate-solubilizing bacteria (PSB) are soil bacteria that convert phosphorus already present in soil from forms plants cannot take up into forms they can. Most soil phosphorus is not in solution: it is bound as calcium phosphates in alkaline and calcareous soils, as iron and aluminium phosphates in acid soils, and as organic phosphorus in residues and humus. PSB act on all three — secreting organic acids that dissolve mineral-bound phosphate and lower the pH immediately around them, and releasing phosphatase and phytase enzymes that mineralise organic phosphorus. Genera used commercially include Bacillus and Pseudomonas.',
    section: 'Soil biology',
    updated: '2026-09-12',
    body: [
      {
        heading: 'Why phosphorus needs solubilising at all',
        paras: [
          'Phosphorus is both essential and notoriously immobile. A phosphate ion in soil solution diffuses only a fraction of a millimetre before it is adsorbed onto a mineral surface or precipitated, so a root has to grow to phosphorus rather than wait for it to arrive. Applied phosphate fertiliser is subject to the same chemistry: a large part of it is fixed into less-available pools rather than being taken up in the season it is applied.',
          'The consequence is that many soils hold a substantial total phosphorus stock alongside a low available reading. Solubilising activity works on that gap — it does not add phosphorus, it releases what is already there.',
        ],
      },
      {
        heading: 'The mechanisms',
        bullets: [
          'Organic acid production — gluconic, citric, oxalic and other acids chelate the calcium, iron or aluminium holding phosphate, and lower local pH. This is the dominant mechanism for mineral-bound phosphorus.',
          'Proton extrusion — acidification of the immediate surroundings during ammonium uptake and metabolism, which shifts phosphate solubility.',
          'Enzymatic mineralisation — phosphatases and phytases cleave phosphate from organic compounds, including phytate, which can hold a large share of soil organic phosphorus.',
          'Siderophore production and chelation — binding iron, which indirectly frees phosphate held in iron phosphates.',
        ],
      },
      {
        heading: 'What PSB do beyond phosphorus',
        paras: [
          'The organisms used as PSB are generally plant growth-promoting rhizobacteria with more than one activity. The same Bacillus species are commonly documented as producing hydrolytic enzymes that break down organic matter, occupying root-surface space in competition with pathogens, and in some cases producing compounds that influence root architecture.',
          'This is worth stating carefully. A bacterium having a documented capability in culture is not the same as that capability producing a measurable field effect in your soil. Registration specifications describe what the product contains, not what it will achieve.',
        ],
      },
      {
        heading: 'How PSB products are specified',
        paras: [
          'PSB products are specified by viable count. Indian bio-fertilizer specifications under the Fertiliser (Control) Order distinguish liquid from carrier-based formulations, and product labels state the minimum count against that standard — GreenbayBio’s liquid PSB sheets state a minimum of 1.00 × 10⁸ cfu/ml, and the carrier-based powder 1.00 × 10⁷ cfu/gm, each described as being as per FCO.',
          'Because the count is what is being sold, shelf stability is part of the formulation problem. Encapsulating the bacteria as dormant spores is one answer, and it is why some liquid PSB products call for mixing with water and standing for a period before application: the step exists to bring the spores out of dormancy, and skipping it applies dormant cells.',
        ],
      },
      {
        heading: 'Getting the conditions right',
        bullets: [
          'Moisture — solubilising activity and bacterial survival both need water. Applying to dry soil without watering in wastes the application.',
          'Timing relative to pesticides — fungicides in particular are lethal to the active ingredient. Labels specify an interval; GreenbayBio’s PSB labels specify at least 48 hours either side.',
          'Sunlight and temperature — direct sunlight and freezing both reduce viable counts in storage and in the tank.',
          'Organic matter — solubilisers work on a phosphorus stock and need a carbon supply. Soils very low in organic matter give introduced organisms less to work with.',
        ],
      },
    ],
    rangeNote:
      'AGN and AGN LTE are GreenbayBio’s liquid PSB bio-fertilizers — the same Bacillus amyloliquefaciens and Bacillus megaterium formulation on two different crop programmes — and BIODYNAMIC is the carrier-based powder. Counts, dilutions, incubation times, crop schedules and approvals are on each product page.',
    relatedProductSlugs: ['agn', 'agn-lte', 'biodynamic'],
    relatedCategorySlugs: ['soil-biology'],
    relatedSlugs: ['bio-fertilizers', 'rhizosphere', 'soil-biology'],
    faq: [
      {
        q: 'Does PSB mean I can stop applying phosphate fertiliser?',
        a: 'No. PSB release phosphorus that is already in the soil; they do not add any. How much of your phosphate programme that offsets depends on the soil’s total phosphorus stock, its pH and chemistry, and the crop — which is a soil-test and agronomy question, not something a product label can answer.',
      },
      {
        q: 'What does 1 × 10⁸ cfu/ml mean?',
        a: 'It is the minimum number of viable, colony-forming bacterial cells per millilitre of product — a hundred million per millilitre in this case. It is the specification the product is sold against, measured at manufacture and expected to hold through the stated shelf life.',
      },
      {
        q: 'Why does a PSB liquid have to be incubated before use?',
        a: 'Where a label calls for it, the bacteria are held as dormant, encapsulated spores so the count stays stable in storage. Mixing with water and standing for the stated time activates them. Applying without that step applies dormant cells.',
      },
    ],
    sources: [
      FCO,
      'Peer-reviewed literature on phosphate-solubilizing microorganisms and soil phosphorus chemistry',
      'Indian Council of Agricultural Research (ICAR) — bio-fertiliser specifications and extension material',
    ],
    keywords: [
      'phosphate solubilizing bacteria',
      'PSB',
      'PSB bio-fertilizer',
      'Bacillus megaterium',
      'Bacillus amyloliquefaciens',
      'soil phosphorus availability',
    ],
  },

  // ──────────────────────────────────────────── humic and fulvic substances
  {
    slug: 'humic-and-fulvic-substances',
    title: 'What are humic and fulvic substances?',
    summary:
      'Humic substances are the stable organic fraction left after decomposition. Humic acid, fulvic acid and humin are separated by solubility; fulvic is the smallest and most mobile, and the strongest chelator.',
    answer:
      'Humic substances are the dark, chemically complex organic materials that remain after plant and microbial residues decompose — the stable fraction of soil organic matter. They are classified by how they behave in solution rather than by a single molecular formula: fulvic acid stays soluble at any pH, humic acid dissolves in alkali but precipitates in acid, and humin does not dissolve at all. Fulvic fractions have the lowest molecular weight and the highest density of oxygen-containing groups, which makes them the most mobile and the strongest chelators of metal nutrients.',
    section: 'Bio-inputs',
    updated: '2026-09-12',
    body: [
      {
        heading: 'A classification by extraction, not chemistry',
        paras: [
          'It is worth being clear about what these names are. Humic acid, fulvic acid and humin are operational categories defined by an alkali-then-acid extraction procedure, not distinct compounds. Each fraction is a mixture of many molecules with a range of sizes and functional groups.',
          'Modern soil-organic-matter research has revised parts of the older picture — in particular, whether large stable "humic macromolecules" exist in soil as such, or whether the extracted fractions are assemblages produced partly by the extraction itself. That debate does not change the practical behaviour of the materials sold under these names, but it is a reason to be wary of confident mechanistic claims.',
        ],
      },
      {
        heading: 'What they do',
        terms: [
          {
            term: 'Chelation',
            detail:
              'Carboxyl and phenolic groups bind metal ions — iron, zinc, manganese, copper, calcium — holding them in solution instead of letting them precipitate. Chelated micronutrients stay available and mobile for longer, which is the most consistently reported effect.',
          },
          {
            term: 'Cation exchange',
            detail:
              'The same acidic groups give humic materials a high cation exchange capacity, contributing to a soil’s ability to hold and release nutrient cations rather than lose them to leaching.',
          },
          {
            term: 'Root development',
            detail:
              'Humic and fulvic applications are frequently associated with increased root length and branching. Proposed mechanisms include hormone-like activity and effects on membrane proton pumps; the size of the response varies widely between trials.',
          },
          {
            term: 'Soil structure and water',
            detail:
              'As part of soil organic matter, humic fractions contribute to aggregate stability and water-holding capacity. This is a slow, cumulative effect of organic-matter status rather than a response to a single application.',
          },
        ],
      },
      {
        heading: 'Humic or fulvic — what the difference means in practice',
        bullets: [
          'Molecular size — fulvic fractions are smaller, which is why they remain soluble across the pH range and move more readily in soil and within the plant.',
          'Functional group density — fulvic fractions carry more carboxyl and phenolic groups per unit mass, giving stronger chelation per gram.',
          'Where each is typically used — fulvic-dominant products are commonly applied in fertigation and foliar programmes where mobility matters; humic-dominant products are more often used as soil amendments aimed at organic-matter and exchange properties.',
          'Concentration is not comparable across labels unless the analytical method is the same. Humic and fulvic assays differ, and a percentage from one method cannot be read against a percentage from another.',
        ],
      },
      {
        heading: 'Where they come from',
        paras: [
          'Commercial humic and fulvic products are extracted from geological sources such as leonardite and lignite, or from composted and humified plant material. The source affects the ratio of fractions, the ash content and the trace-element profile — which is why a heavy-metal and residue statement is a relevant thing to look for on a specification sheet.',
        ],
      },
    ],
    rangeNote:
      'Fulvic 25 is the fulvic and humic product in GreenbayBio’s biostimulant category; Biovin is a humified organic soil improver made from grape must. Their pages state the analytical figures each product is specified against, its documented crops and rates, and its approval.',
    relatedProductSlugs: ['fulvic-25', 'biovin'],
    relatedCategorySlugs: ['biostimulants', 'soil-improvers'],
    relatedSlugs: ['biostimulants', 'soil-biology', 'foliar-and-soil-application'],
    faq: [
      {
        q: 'Is fulvic acid better than humic acid?',
        a: 'Neither is better in general; they behave differently. Fulvic fractions are smaller, stay soluble at any pH and chelate more strongly per gram, which suits fertigation and foliar use. Humic fractions are more often used for soil organic-matter and exchange-capacity purposes. The right choice follows from what you are trying to do.',
      },
      {
        q: 'Do humic products supply nutrients?',
        a: 'Only incidentally. Their function is to hold, chelate and exchange nutrients and to influence root development and soil structure — which is why they are classified as biostimulants rather than fertilisers, and why their nutrient analysis is not the measure of them.',
      },
      {
        q: 'Can humic and fulvic products be mixed with fertilisers?',
        a: 'Often, but not always, and concentrated calcium or phosphate solutions are a known compatibility risk with some humic materials. Check the product label and jar-test an unfamiliar combination before mixing a full tank.',
      },
    ],
    sources: [
      'Standard soil-chemistry texts on soil organic matter and humic substances',
      'International Humic Substances Society — the reference framework for humic fraction definitions',
      'Peer-reviewed literature reassessing the humic-macromolecule model of soil organic matter',
    ],
    keywords: ['humic acid', 'fulvic acid', 'humic substances', 'chelation', 'cation exchange capacity'],
  },

  // ───────────────────────────────────────────── amino acid biostimulants
  {
    slug: 'amino-acid-biostimulants',
    title: 'What are amino-acid biostimulants and protein hydrolysates?',
    summary:
      'Protein hydrolysates supply free amino acids and short peptides from hydrolysed plant or animal protein. They act as directly usable nitrogen, as chelators and as signals — and plant-source products differ from animal-source ones.',
    answer:
      'An amino-acid biostimulant, more precisely a protein hydrolysate, is made by breaking protein down — enzymatically or chemically — into free amino acids and short peptides that a plant can absorb and use directly. Its value is not that it supplies nitrogen in quantity; a protein hydrolysate is applied at rates far too low for that. It is that the nitrogen it supplies is already in amino-acid form, so the plant does not have to spend energy reducing nitrate and assembling amino acids itself — which matters most when metabolism is slowed by cold, drought or another stress.',
    section: 'Bio-inputs',
    updated: '2026-09-12',
    body: [
      {
        heading: 'How they are made, and why that matters',
        paras: [
          'Hydrolysis can be enzymatic or chemical, and the route affects the product. Enzymatic hydrolysis is milder and preserves amino acids in their biologically active L-form; harsh chemical hydrolysis can racemise some amino acids and destroy others, and typically leaves a higher salt load.',
          'The protein source matters too. Plant-derived hydrolysates come from residues such as seed meals and other plant by-products; animal-derived ones from collagen, hides or blood. Plant-source products are generally lower in sodium and chloride, which is relevant because high salt load in a foliar spray or a fertigation line is what causes leaf scorch and osmotic stress on roots. Some organic certification standards also treat plant- and animal-derived inputs differently, so where a product is used in a certified system, the approval named on its own label is the only reliable guide.',
        ],
      },
      {
        heading: 'The proposed modes of action',
        bullets: [
          'Directly usable nitrogen — amino acids can be taken up by roots and leaves and incorporated without the energy cost of nitrate reduction and amino-acid synthesis.',
          'Chelation — amino acids and peptides complex metal micronutrients, keeping them soluble and available.',
          'Signalling and hormone-like activity — specific peptides have been shown to influence root architecture, nutrient-transporter expression and stress-response pathways.',
          'Osmotic and antioxidant contribution — proline and some other amino acids accumulate in plants under stress and act as compatible solutes, which is part of the rationale for applying them ahead of a predictable stress period.',
          'Soil and rhizosphere effects — the carbon and nitrogen in a hydrolysate is also a substrate for soil microorganisms, so a soil-applied product feeds biological activity as well as the crop.',
        ],
      },
      {
        heading: 'Reading a hydrolysate label',
        paras: [
          'Three figures carry most of the information. Total amino acids says how much amino-acid material is present. Free amino acids says how much of it is already in single-molecule form and immediately usable — the more informative of the two. Total organic carbon indicates the organic load.',
          'Beyond those, pH and specific gravity matter for tank compatibility, and solubility matters for fertigation. What a percentage figure means also depends on the method used to measure it, so figures from different products are only strictly comparable when the method is stated and the same.',
        ],
      },
      {
        heading: 'Application practice',
        paras: [
          'Protein hydrolysates are applied both as foliar sprays and through fertigation or drenching, at defined crop stages rather than continuously. They are commonly timed to establishment, to periods of rapid growth, and to the run-up to a known stress — a cold spell, a heat event, transplanting.',
          'Two practical points recur on labels. Some hydrolysates settle or separate in the container and need shaking or stirring before use. And once diluted, some have a limited window before application because the organic material is itself a substrate for microbial growth in the tank — GreenbayBio’s AminOPF sheet, for example, specifies applying within four to six hours of mixing. These are label instructions, not general rules.',
        ],
      },
    ],
    rangeNote:
      'AminOPF is the protein hydrolysate in GreenbayBio’s biostimulant category — a plant-source hydrolysate. Its page states its amino-acid figures, pH and specific gravity, the crops and rates documented for it, and its approval.',
    relatedProductSlugs: ['aminopf', 'fulvic-25'],
    relatedCategorySlugs: ['biostimulants'],
    relatedSlugs: ['biostimulants', 'humic-and-fulvic-substances', 'foliar-and-soil-application'],
    faq: [
      {
        q: 'Are amino-acid products a nitrogen fertiliser?',
        a: 'No. They supply nitrogen in amino-acid form but at rates far below a nitrogen fertiliser’s. They are used to improve how efficiently a crop builds and uses nitrogen compounds, not to meet its nitrogen requirement.',
      },
      {
        q: 'What is the difference between total and free amino acids?',
        a: 'Total amino acids counts all amino-acid material, including what is still bound in peptides. Free amino acids counts only single molecules, which the plant can use without further breakdown. For a biostimulant, the free figure is the more informative of the two.',
      },
      {
        q: 'Why do some amino-acid products have to be applied soon after mixing?',
        a: 'Because the diluted product is an attractive substrate for microorganisms, and a tank left standing can ferment. Where a label states a window — AminOPF specifies four to six hours — mix only what you will apply within it.',
      },
      {
        q: 'Do amino-acid biostimulants work in cold weather?',
        a: 'This is the situation they are most often used in, on the reasoning that supplying ready-made amino acids helps when a plant’s own synthesis is slowed. The size of the benefit depends on the crop, the severity and the rest of the nutrition programme, so it is better treated as a reason to try than as a guaranteed result.',
      },
    ],
    sources: [
      EUFPR,
      'Peer-reviewed literature on protein hydrolysates as plant biostimulants and on amino-acid uptake by plants',
      'Colla, G. et al. — reviews of protein hydrolysate biostimulant action in horticulture',
    ],
    keywords: [
      'amino acid biostimulant',
      'protein hydrolysate',
      'free amino acids',
      'organic plant feed',
      'abiotic stress',
    ],
  },

  // ───────────────────────────────────── foliar vs soil vs fertigation
  {
    slug: 'foliar-and-soil-application',
    title: 'Foliar spray, soil application or fertigation — which and when?',
    summary:
      'Foliar spraying, soil application and fertigation deliver an input to different places by different routes. Each suits different products, quantities and purposes, and each has its own conditions for working properly.',
    answer:
      'Foliar spraying, soil application and fertigation are three delivery routes, not three strengths of the same thing. A foliar spray puts a small quantity of a soluble input onto leaf surfaces, where it enters through the cuticle and stomata — fast-acting, but limited in how much can be delivered. Soil application places the input in the root zone, where the quantities can be much larger but availability depends on soil chemistry, moisture and biology. Fertigation delivers a fully soluble input through the irrigation system, distributing it evenly in the wetted root volume. Which route a product uses is set by what it is and where it needs to act, and is stated on its label.',
    section: 'Application practice',
    updated: '2026-09-12',
    body: [
      {
        heading: 'Foliar application',
        paras: [
          'Uptake through a leaf is limited by the cuticle — a waxy, water-repellent layer — so foliar application suits inputs needed in small amounts: micronutrients, biostimulants, and correction of a visible deficiency during the season. It is not a practical route for the bulk of a crop’s nitrogen, phosphorus or potassium.',
          'What determines whether a foliar spray works is mostly application quality rather than product choice: coverage, droplet size, spray volume and how long the leaf stays wet. Uptake continues while the deposit is moist, which is why early morning and late evening are the usual windows and midday heat and wind are not — the spray dries before much of it is absorbed.',
        ],
        bullets: [
          'Spray when evaporation is slow — early morning or evening — and avoid high wind.',
          'Cover the foliage evenly, including leaf undersides where the product’s label calls for it.',
          'Use the water volume the label specifies. Concentration and volume together determine both the dose delivered and the risk of scorch.',
          'Where the label gives a window between mixing and spraying, respect it.',
        ],
      },
      {
        heading: 'Soil application',
        paras: [
          'Soil application — broadcasting, banding, drenching or incorporating — places the input where roots are and allows much larger quantities. The trade-off is that what happens next is decided by the soil: pH governs the solubility of phosphorus and most micronutrients, cation exchange capacity governs how well cations are retained, moisture governs movement and microbial activity, and organic matter feeds it.',
          'For living products the same logic is sharper. A microbial input has to arrive in the root zone in water and alive, so soil-applied bio-fertilizers specify a dilution and an irrigation or drench method, and where the label calls for an incubation step before application, that step is what activates dormant cells.',
        ],
      },
      {
        heading: 'Fertigation',
        paras: [
          'Fertigation applies the input dissolved in irrigation water, which distributes it through the wetted root volume and allows dose to be split across many small applications instead of a few large ones. It suits drip and micro-irrigation particularly well.',
          'It also imposes requirements. The product must be fully soluble and compatible with whatever else is in the line — precipitates block emitters and are difficult to clear. Order of mixing matters. And the irrigation set has to be long enough to carry the input into the root zone but not so long that a mobile nutrient is flushed past it.',
        ],
      },
      {
        heading: 'Choosing between them',
        terms: [
          {
            term: 'Act on the soil or the root zone',
            detail: 'Soil application or fertigation. A product formulated for the rhizosphere cannot do its work on a leaf.',
          },
          {
            term: 'Act on the plant, quickly, in small quantity',
            detail: 'Foliar. Micronutrient correction and stage-timed biostimulant applications are the typical cases.',
          },
          {
            term: 'Deliver bulk nutrition',
            detail: 'Soil application or fertigation. Foliar cannot carry the quantities involved.',
          },
          {
            term: 'Spread a dose across the season',
            detail: 'Fertigation, where an irrigation system exists — many small applications instead of a few large ones.',
          },
        ],
      },
      {
        heading: 'Mixing and intervals',
        paras: [
          'Tank compatibility is product-specific and belongs to the label, not to general reasoning. The recurring rule for living products is an interval, not a mixture: microbial bio-fertilizers are killed by fungicides and many pesticides, so labels specify a gap either side. GreenbayBio’s bio-fertilizer labels specify at least 48 hours.',
          'Where a product states that it may be applied with fertilisers, microbial products or plant enhancers, that statement is on its own page. Where it does not, jar-test an unfamiliar combination in the proportions you intend to use before committing a tank — and where the crop, the soil or the water is unusual, ask a crop advisor rather than extrapolating.',
        ],
      },
    ],
    rangeNote:
      'Every GreenbayBio product page states the application methods documented for that product, its dose, and its crop-by-crop timing where a schedule exists. The Usage and application guides page collects the practice notes that apply across the range.',
    relatedProductSlugs: ['agn', 'aminopf', 'fulvic-25', 'c4l-biofoliar', 'nature-s-green-4-plus', 'hydretain'],
    relatedCategorySlugs: ['soil-biology', 'biostimulants', 'plant-strength', 'water-management'],
    relatedSlugs: ['rhizosphere', 'biostimulants', 'bio-fertilizers'],
    faq: [
      {
        q: 'Is foliar feeding better than soil application?',
        a: 'Neither is better in general — they deliver different quantities to different places. Foliar is for small quantities acting quickly on the plant; soil and fertigation are for larger quantities acting through the root zone. A product formulated for one route will not work through the other.',
      },
      {
        q: 'What is fertigation?',
        a: 'Applying a soluble input through the irrigation system, so it is delivered dissolved in water across the wetted root volume. It requires a fully soluble, compatible product and care over mixing order and irrigation timing.',
      },
      {
        q: 'What is the best time of day to spray?',
        a: 'Early morning or late evening, when evaporation is slow and wind is usually lower. Uptake through the leaf continues while the deposit stays moist, so a spray that dries in minutes delivers a fraction of its dose.',
      },
      {
        q: 'Can I tank-mix a bio-fertilizer with a fungicide?',
        a: 'No — the active ingredient is alive and the fungicide is likely to kill it. Labels specify an interval instead; GreenbayBio’s bio-fertilizer labels specify at least 48 hours before or after.',
      },
    ],
    sources: [
      'Standard plant-nutrition texts on foliar uptake and root uptake mechanisms',
      'Extension guidance on spray application quality, fertigation practice and irrigation scheduling',
      'Food and Agriculture Organization of the United Nations (FAO) — irrigation and plant-nutrition guidance',
    ],
    keywords: [
      'foliar application',
      'foliar spray',
      'fertigation',
      'soil application',
      'drenching',
      'spray timing',
    ],
  },
];

export const knowledgeBySlug = new Map(knowledgeArticles.map((a) => [a.slug, a]));

export function knowledgeArticle(slug: string): KnowledgeArticle | undefined {
  return knowledgeBySlug.get(slug);
}

/**
 * Articles relevant to a product — by explicit product link, or by its category. Product slugs are
 * matched through the alias table so a renamed product keeps its science links.
 */
export function articlesForProduct(productSlug: string, categorySlug?: string): KnowledgeArticle[] {
  const want = canonicalProductSlug(productSlug);
  return knowledgeArticles.filter(
    (a) =>
      a.relatedProductSlugs.some((s) => canonicalProductSlug(s) === want) ||
      (!!categorySlug && a.relatedCategorySlugs.includes(categorySlug)),
  );
}

/** Articles relevant to a product category. */
export function articlesForCategory(categorySlug: string): KnowledgeArticle[] {
  return knowledgeArticles.filter((a) => a.relatedCategorySlugs.includes(categorySlug));
}

/** Plain text of an article, for the llms.txt corpus and the Markdown mirrors. */
export function articleSections(a: KnowledgeArticle): KnowledgeSection[] {
  return a.body;
}
